import React, { memo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { UserPlus, User, Mail, Clock, CheckCircle, Wrench, Search, Building, Calendar, Folder } from 'lucide-react';
import { WorkOrderWithBuilding, WorkOrderPriority, workOrderPriorityColors, workOrderPriorityLabels, getAssignees, getDelegations } from '../../../types/workOrders';
import { AssigneeMeta, DelegationMeta } from '../../../types/assignees';
import { BuildingCheckWithTemplate, checkDisplayName, getDaysUntilDue } from '../../BuildingChecks/buildingChecksUtils';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import { useOpenPath } from '../../../hooks/useOpenPath';

// Unified task type that can represent either work order or inspection
export type TaskType = 'workOrder' | 'inspection';

export interface UnifiedTask {
    type: TaskType;
    key: string;
    title: string;
    buildingKey: string;
    buildingName: string;
    dueDate?: string;
    priority?: WorkOrderPriority;
    // Multi-assignee collections (normalized; tolerant of legacy scalars).
    assignees?: { [uid: string]: AssigneeMeta };
    delegations?: { [externalUserId: string]: DelegationMeta };
    // Legacy scalars — kept for inspections and any not-yet-migrated reads.
    assignedTo?: string;  // UID for internal user
    externalUserId?: string;  // UID for external user
    guestTokenKey?: string;  // Token key for external delegation (needed for revocation)
    isOverdue: boolean;
    // Set for work orders that are delmoment of a project (shown only when the
    // board opts in to project delmoment): the pill reads "Delmoment" and the
    // project name is listed under the title.
    projectTitle?: string;
    // Original data for actions
    workOrder?: WorkOrderWithBuilding;
    inspection?: BuildingCheckWithTemplate;
}

interface TaskCardProps {
    task: UnifiedTask;
    onClick?: () => void;
    /** When set, the card opens this page instead of calling onClick (Cmd/Ctrl/middle click: new tab). */
    href?: string;
    onAssign?: (task: UnifiedTask) => void;
    onSetDueDate?: (task: UnifiedTask) => void;
    getUserName?: (uid: string) => string;
    isSelected?: boolean;
    isCompleted?: boolean;  // Show completed state: completion date, completed by, no overdue styling
}

/**
 * Get assignment display text (multi-assignee): a single name when there is
 * exactly one recipient, otherwise a compact count like "3 tilldelade".
 */
function getAssignmentDisplay(task: UnifiedTask, getUserName?: (uid: string) => string): string | null {
    if (!getUserName) return null;
    const assigneeUids = Object.keys(getAssignees(task));
    const delegateUids = Object.keys(getDelegations(task));
    const total = assigneeUids.length + delegateUids.length;

    if (total === 0) return null;
    if (total === 1) {
        if (assigneeUids.length === 1) return getUserName(assigneeUids[0]);
        return `${getUserName(delegateUids[0])} (extern)`;
    }
    return `${total} tilldelade`;
}

/**
 * Comma-separated assignee/delegate names for a task (delegates suffixed
 * "(extern)"), or null when unassigned. Shared by the task detail views.
 */
export function getAssignmentNames(task: UnifiedTask, getUserName: (uid: string) => string): string | null {
    const names = [
        ...Object.keys(getAssignees(task)).map((uid) => getUserName(uid)),
        ...Object.keys(getDelegations(task)).map((uid) => `${getUserName(uid)} (extern)`),
    ];
    return names.length > 0 ? names.join(', ') : null;
}

/**
 * Get completion display text for completed work orders
 */
function getCompletionDisplay(task: UnifiedTask, getUserName?: (uid: string) => string): string | null {
    const wo = task.workOrder;
    if (!wo?.completedBy || !getUserName) return null;
    const name = getUserName(wo.completedBy);
    return `Slutförd av ${name}`;
}

/**
 * Card displaying a unified task (work order or inspection) for the Kanban board
 * Memoized to prevent unnecessary re-renders when parent state changes
 */
const TaskCard: React.FC<TaskCardProps> = memo(({
    task,
    onClick,
    href,
    onAssign,
    onSetDueDate,
    getUserName,
    isSelected,
    isCompleted
}) => {
    const assignmentDisplay = getAssignmentDisplay(task, getUserName);
    const completionDisplay = isCompleted ? getCompletionDisplay(task, getUserName) : null;
    const isExternalAssignment = Object.keys(getDelegations(task)).length > 0;

    const openPath = useOpenPath();

    const handleClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.task-card__action-btn')) {
            return;
        }
        if (href) openPath(href, e);
        else onClick?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (href) openPath(href);
            else onClick?.();
        }
    };

    const handleAssignClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onAssign?.(task);
    };

    const handleDueDateClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSetDueDate?.(task);
    };

    // Don't show overdue styling for completed tasks
    const showOverdue = task.isOverdue && !isCompleted;

    return (
        <div
            className={`management-item ${showOverdue ? 'management-item--overdue' : ''} ${isSelected ? 'management-item--selected' : ''}`}
            onClick={handleClick}
            onAuxClick={href ? handleClick : undefined}
            role={href ? 'link' : 'button'}
            tabIndex={0}
            onKeyDown={handleKeyDown}
        >
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 me-2">
                    <div className="d-flex align-items-center gap-1 mb-1">
                        {/* Task type indicator */}
                        {task.projectTitle ? (
                            <Badge
                                bg="project"
                                className="task-type-badge badge-delmoment d-inline-flex align-items-center gap-1"
                            >
                                <Folder size={10} />
                                Delmoment
                            </Badge>
                        ) : (
                            <Badge
                                bg={task.type === 'workOrder' ? 'workOrder' : 'inspection'}
                                className={`task-type-badge d-inline-flex align-items-center gap-1${task.type === 'workOrder' ? ' badge-work-order' : ' badge-inspection'}`}
                            >
                                {task.type === 'workOrder' && <Wrench size={10} />}
                                {task.type === 'inspection' && <Search size={10} />}
                                {task.type === 'workOrder' ? 'Ärende' : 'Kontroll'}
                            </Badge>
                        )}
                    </div>
                    <div className="fw-medium">{task.title}</div>
                    {task.projectTitle && (
                        <div className="small text-muted d-flex align-items-center gap-1"><Folder size={12} />{task.projectTitle}</div>
                    )}
                    <div className="mt-1 small text-muted d-flex align-items-center gap-1"><Building size={12} />{task.buildingName}</div>
                </div>
                <div className="d-flex align-items-start gap-1">
                    {/* Priority badge (only for work orders) */}
                    {task.priority && (
                        <Badge bg={workOrderPriorityColors[task.priority]} className="priority-badge">
                            {workOrderPriorityLabels[task.priority]}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Completion indicator (for completed tasks) */}
            {isCompleted && completionDisplay && (
                <div className="mt-2 d-flex align-items-center gap-1 small text-muted">
                    <CheckCircle size={12} />
                    <span>{completionDisplay}</span>
                </div>
            )}

            {/* Assignment indicator (for active tasks) */}
            {!isCompleted && assignmentDisplay && (
                <div className="mt-2 d-flex align-items-center gap-1 small text-muted">
                    {isExternalAssignment ? (
                        <Mail size={12} />
                    ) : (
                        <User size={12} />
                    )}
                    <span>{assignmentDisplay}</span>
                </div>
            )}

            {/* Date section */}
            {(isCompleted ? !!task.workOrder?.completedAt : !!task.dueDate) && (
                <div className="mt-2 d-flex align-items-center">
                    {isCompleted && task.workOrder?.completedAt && (
                        <small className="text-muted d-flex align-items-center gap-1">
                            <Clock size={12} />
                            Slutförd: {formatDateForDisplay(task.workOrder.completedAt)}
                        </small>
                    )}
                    {!isCompleted && task.dueDate && (
                        <small className={`d-flex align-items-center gap-1 ${showOverdue ? '' : 'text-muted'}`} style={showOverdue ? { color: '#dc3545' } : undefined}>
                            <Clock size={12} />
                            {formatDateForDisplay(task.dueDate)}
                        </small>
                    )}
                </div>
            )}

            {/* Due date button — only work orders support editing a due date directly */}
            {!isCompleted && task.type === 'workOrder' && onSetDueDate && (
                <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-muted task-card__action-btn task-card__duedate-btn"
                    style={{ lineHeight: 1 }}
                    onClick={handleDueDateClick}
                    title={task.dueDate ? 'Ändra slutdatum' : 'Sätt slutdatum'}
                >
                    <Calendar size={18} />
                </Button>
            )}

            {/* Assign button — absolute bottom-right corner */}
            {!isCompleted && onAssign && (
                <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-muted task-card__action-btn task-card__assign-btn"
                    style={{ lineHeight: 1 }}
                    onClick={handleAssignClick}
                    title={assignmentDisplay ? 'Ändra tilldelning' : 'Tilldela'}
                >
                    <UserPlus size={18} />
                </Button>
            )}
        </div>
    );
});

TaskCard.displayName = 'TaskCard';

export default TaskCard;

/**
 * Convert a WorkOrder to UnifiedTask
 */
export function workOrderToTask(wo: WorkOrderWithBuilding, projectTitle?: string): UnifiedTask {
    const isOverdue = wo.dueDate && wo.status === 'open' &&
        new Date(wo.dueDate.replace(' ', 'T')) < new Date();

    return {
        type: 'workOrder',
        key: wo.key,
        title: wo.title,
        buildingKey: wo.buildingKey,
        buildingName: wo.buildingName,
        dueDate: wo.dueDate,
        priority: wo.priority,
        // Normalized multi-assignee collections (fold legacy scalars).
        assignees: getAssignees(wo),
        delegations: getDelegations(wo),
        // Legacy scalars retained for any direct reads.
        assignedTo: wo.assignedTo,
        externalUserId: wo.externalUserId,
        guestTokenKey: wo.guestTokenKey,
        isOverdue: !!isOverdue,
        projectTitle,
        workOrder: wo
    };
}

/**
 * Convert a BuildingCheck to UnifiedTask. `componentName` names the check's component
 * when the caller has it (see checkDisplayName).
 */
export function inspectionToTask(check: BuildingCheckWithTemplate, componentName?: (key: string) => string | undefined): UnifiedTask {
    const daysUntilDue = getDaysUntilDue(check.nextDueDate);
    const isOverdue = daysUntilDue < 0;

    return {
        type: 'inspection',
        key: `${check.buildingId}-${check.checkKey}`,
        title: check.template?.name
            ? checkDisplayName(check, check.template.name, { componentName })
            : 'Namnlös kontroll',
        buildingKey: check.buildingId || '',
        buildingName: check.buildingName || 'Okänd byggnad',
        dueDate: check.nextDueDate,
        priority: undefined,  // Inspections don't have priority
        assignedTo: undefined,  // Will be added when inspection delegation is implemented
        isOverdue,
        inspection: check
    };
}
